import { useState, type FormEvent } from 'react';
import { ApiError } from '@/api/client';
import { Icon } from '@/components/Icon';
import { money, num, qty } from '@/lib/format';
import { createPurchaseAdjustment } from './api';
import type { PurchaseOrderDetail } from './types';
import s from './PurchaseAdjustmentDialog.module.css';

export function PurchaseAdjustmentDialog({ order, initialKind, onClose, onSuccess }: {
  order: PurchaseOrderDetail;
  initialKind: 'REJECTION' | 'RETURN';
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [kind, setKind] = useState(initialKind);
  const [lineId, setLineId] = useState('');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const eligible = order.lines.filter((line) => kind === 'RETURN' ? num(line.receivedQuantity) > 0 : num(line.quantity) - num(line.receivedQuantity) > 0);
  const line = eligible.find((item) => item.id === lineId);
  const max = line ? kind === 'RETURN' ? num(line.receivedQuantity) : num(line.quantity) - num(line.receivedQuantity) : 0;
  const quantity = Number(amount);
  const valid = line && Number.isFinite(quantity) && quantity > 0 && Math.round(quantity * 1000) === quantity * 1000 && quantity <= max + 0.0001 && reason.trim();
  const title = kind === 'RETURN' ? '采购退货' : '到货拒收';
  const close = () => { onClose(); if (done) onSuccess(); };

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!valid || saving || uncertain) return;
    setSaving(true);
    setError('');
    try {
      await createPurchaseAdjustment(order.id, { kind, lineId, quantity, reason: reason.trim() });
      setDone(true);
    } catch (cause) {
      if (!(cause instanceof ApiError) || cause.status >= 500) setUncertain(true);
      setError(cause instanceof Error ? cause.message : '保存失败，请核对记录后重试');
    } finally { setSaving(false); }
  }

  return <div className={s.backdrop} onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) close(); }}>
    <section className={s.dialog} role="dialog" aria-modal="true" aria-label={`${title} · ${order.purchaseNo}`}>
      <header><h2>{title} <small>{order.purchaseNo}</small></h2><button type="button" disabled={saving} onClick={close} aria-label="关闭"><Icon name="x" size={18} /></button></header>
      {done ? <div className={s.done}><h3>{title}已记录</h3><p>关闭窗口后会重新读取采购单、库存与对账数据。</p><button type="button" onClick={close}>查看采购单</button></div> :
        <form onSubmit={(event) => void submit(event)}>
          <div className={s.body}>
            <p className={s.hint}>{kind === 'REJECTION' ? '拒收货物不进入库存，不计入应付；本月对账会列出到货申报和等额拒收冲减。' : '退货将扣减已入库数量与当前库存，在退货发生月份生成负数应付；若当前库存不足，保存会被拒绝。'}</p>
            <div className={s.kind}><button type="button" className={kind === 'REJECTION' ? s.active : ''} onClick={() => { setKind('REJECTION'); setLineId(''); setAmount(''); }}>到货拒收</button><button type="button" className={kind === 'RETURN' ? s.active : ''} onClick={() => { setKind('RETURN'); setLineId(''); setAmount(''); }}>已入库退货</button></div>
            <label>采购货物<select required value={lineId} onChange={(event) => { setLineId(event.target.value); setAmount(''); }}><option value="">请选择</option>{eligible.map((item) => <option key={item.id} value={item.id}>{item.product.name} · {item.product.code} · {kind === 'RETURN' ? '可退' : '待入库'} {qty(kind === 'RETURN' ? item.receivedQuantity : num(item.quantity) - num(item.receivedQuantity))}</option>)}</select></label>
            <label>数量<input required type="number" min="0.001" max={max || undefined} step="0.001" value={amount} onChange={(event) => setAmount(event.target.value)} placeholder="最多三位小数" /></label>
            <label>原因<textarea required maxLength={500} rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="例如破损、规格不符、供应商召回" /></label>
            {line && quantity > 0 && <p className={s.preview}>数量 {qty(quantity)} · 记录货值 {money(quantity * num(line.unitCost))}{kind === 'REJECTION' ? ' · 净应付 0' : ' · 冲减本月应付'}</p>}
            {error && <p className={s.error} role="alert">{error}{uncertain && '。请求结果未确认，请先核对采购记录，暂勿重复提交。'}</p>}
          </div>
          <footer><button type="button" onClick={close} disabled={saving}>取消</button><button type="submit" disabled={!valid || saving || uncertain}>{saving ? '正在保存…' : `确认${title}`}</button></footer>
        </form>}
    </section>
  </div>;
}
